import genericAvatar from "assets/images/generic_avatar.jpg";
import { ErrorModal, PhotoUploader } from "components";
import { getDownloadURL } from "firebase/storage";
import { emptyFaculty, Faculty } from "models";
import React, { useState } from "react";
import { Button, Col, Form, Modal } from "react-bootstrap";
import Img from "react-cool-img";
import { addFacultyStory, updateFacultyStory, uploadProfilePhoto } from "services";

interface AddEditFacultyModalProps {
  /** Whether the modal should be shown. */
  show: boolean;

  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  /** Function to be called when canceling or closing the modal. */
  // onCancel: () => void;
  /** The alum to edit, or undefined if creating a new alum. */
  currentFaculty: Faculty | undefined;
}

/** Modal for adding/editing curated alumni. */
export function AddEditFacultyModal({
  show,
  setShow,
  currentFaculty,
}: AddEditFacultyModalProps): JSX.Element {
  const [editedFaculty, setEditedFaculty] = useState<Faculty>(
    currentFaculty ? { ...currentFaculty } : { ...emptyFaculty },
  );
  const [validated, setValidated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadedPhoto, setUploadedPhoto] = useState<File | undefined>(undefined);
  const [isReplacingPhoto, setIsReplacingPhoto] = useState(false);
  const isNew = currentFaculty === undefined;
  const [error, setError] = useState<Error | undefined>(undefined);

  editedFaculty.type = "faculty";
  editedFaculty.location = "Holland, Michigan"; // this is a temporary solution because of that one error requiring each faculty to have a location

  const handleClose = () => {
    setShow(false);
  };

  /**
   * This method will handle the submit function
   * First checking if the admin has submitted all the required fields
   * Then upload the photo to firebase storage and upload photo url to firestore with the profile
   * Finally upload the profile to firestore
   */
  async function submitFaculty(event: {
    currentTarget: any;
    preventDefault: () => void;
    stopPropagation: () => void;
  }) {
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.preventDefault();
      event.stopPropagation();
    } else {
      event.preventDefault(); // prevents the page from reloading immediately on submit

      if (uploadedPhoto !== undefined) {
        const uploadTask = uploadProfilePhoto(uploadedPhoto, editedFaculty);
        await uploadTask;
        const url = await getDownloadURL(uploadTask.snapshot.ref);
        editedFaculty.photoUrl = url;
      }

      const submitFn: () => Promise<unknown> = isNew
        ? () => addFacultyStory(editedFaculty)
        : () => updateFacultyStory(editedFaculty.id, editedFaculty);

      submitFn()
        .catch(err => setError(err))
        .finally(() => {
          window.location.reload(); // this is not the best but my window was reloading so hard with history.push
        });
      setIsSubmitting(true);
    }
    setValidated(true);
  }

  return (
    <>
      <Modal show={show} size="lg" className="add-edit-alumni-modal">
        <Form noValidate validated={validated} onSubmit={submitFaculty}>
          <Modal.Header>
            <Modal.Title>{isNew ? "Add New Faculty Member" : "Edit Faculty Member"}</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <Form.Group>
              <strong>Basic Info</strong>
            </Form.Group>
            <Form.Row>
              <Col>
                <FacultyFormGroup
                  attribute="firstName"
                  label="First Name"
                  placeholder="e.g. Michael"
                  onChange={e =>
                    setEditedFaculty({
                      ...editedFaculty,
                      firstName: e.target.value.trim(),
                    })
                  }
                  defaultValue={editedFaculty.firstName || ""}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  Please provide a valid first name.
                </Form.Control.Feedback>
              </Col>

              <Col>
                <FacultyFormGroup
                  type="text"
                  attribute="lastName"
                  label="Last Name"
                  placeholder="e.g. Smith "
                  onChange={e =>
                    setEditedFaculty({
                      ...editedFaculty,
                      lastName: e.target.value.trim(),
                    })
                  }
                  defaultValue={editedFaculty.lastName || ""}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  Please provide a valid last name.
                </Form.Control.Feedback>
              </Col>
            </Form.Row>
            <Form.Row>
              <Col>
                <FacultyFormGroup
                  type="text"
                  attribute="position"
                  label="Position in Department"
                  placeholder="e.g. Special Education Professor"
                  onChange={e =>
                    setEditedFaculty({
                      ...editedFaculty,
                      position: e.target.value.trim(),
                    })
                  }
                  defaultValue={editedFaculty.position || ""}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  Please provide a valid department position.
                </Form.Control.Feedback>
              </Col>

              <Col>
                <FacultyFormGroup
                  attribute="coursesTaught" // this is the attribute that holds the Classes Taught
                  label="Courses Taught"
                  placeholder="e.g. Social Studies"
                  onChange={e =>
                    setEditedFaculty({ ...editedFaculty, coursesTaught: e.target.value.trim() })
                  }
                  defaultValue={editedFaculty.coursesTaught || ""}
                />
              </Col>
            </Form.Row>
            <Form.Group>
              <Form.Label>Profile Photo</Form.Label>
              {!!editedFaculty.photoUrl && !isReplacingPhoto ? (
                <Form.Row>
                  <Col xs={2}>
                    <Img
                      src={editedFaculty.photoUrl || ""}
                      placeholder={genericAvatar}
                      alt="Houston, we have a problem. The image is not displaying correctly."
                      width="100%"
                      loading="lazy"
                      className="img-circle"
                    />
                  </Col>
                  <Col className="d-flex align-items-center">
                    <Button variant="outline-secondary" onClick={() => setIsReplacingPhoto(true)}>
                      Replace
                    </Button>
                  </Col>
                </Form.Row>
              ) : (
                <PhotoUploader onDrop={(photos: File[]) => setUploadedPhoto(photos[0])} />
              )}
            </Form.Group>

            <FacultyFormGroup
              as="textarea"
              rows={6}
              attribute="bio"
              label="Bio"
              placeholder="Enter some interesting information about this faculty member!"
              onChange={e =>
                setEditedFaculty({
                  ...editedFaculty,
                  bio: e.target.value.trim(),
                })
              }
              defaultValue={editedFaculty.bio}
              required
            />

            <Form.Row>
              <Col>
                <FacultyFormGroup
                  attribute="researchArea"
                  label="Research Area"
                  placeholder="e.g. Curriculum Design and Development"
                  onChange={e =>
                    setEditedFaculty({ ...editedFaculty, researchArea: e.target.value.trim() })
                  }
                  defaultValue={editedFaculty.researchArea || ""}
                />
              </Col>
              <Col>
                <FacultyFormGroup
                  attribute="recentPublications"
                  label="1-2 Recent Publications"
                  placeholder="e.g. Efficacy of the Responsive Classroom Approach"
                  onChange={e =>
                    setEditedFaculty({
                      ...editedFaculty,
                      recentPublications: e.target.value.trim(),
                    })
                  }
                  defaultValue={editedFaculty.recentPublications || ""}
                />
              </Col>
            </Form.Row>
            <Form.Row>
              <Col>
                <FacultyFormGroup
                  attribute="email"
                  label="Email"
                  placeholder="e.g. example@example.com"
                  onChange={e =>
                    setEditedFaculty({ ...editedFaculty, email: e.target.value.trim() })
                  }
                  defaultValue={editedFaculty.email || ""}
                />
              </Col>

              <Col>
                <FacultyFormGroup
                  type="number"
                  attribute="facultySince"
                  label="Faculty Member Since"
                  placeholder="e.g. 1993 "
                  onChange={e =>
                    setEditedFaculty({
                      ...editedFaculty,
                      facultySince: e.target.value.trim(),
                    })
                  }
                  defaultValue={editedFaculty.facultySince || ""}
                />
              </Col>
            </Form.Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button variant="outline-primary" type="submit" disabled={isSubmitting}>
              Submit
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
      <ErrorModal error={error} />
    </>
  );
}

interface FacultyFormGroupProps {
  attribute: keyof Faculty;
  type?: "text" | "number";
  as?: "textarea";
  label: string;
  placeholder: string;
  onChange: React.ChangeEventHandler<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>;
  defaultValue: string;
  required?: boolean;
  rows?: number;
}

/** An customized input form for the Faculty */
function FacultyFormGroup({
  attribute,
  label,
  placeholder,
  onChange,
  defaultValue,
  type,
  as,
  required,
  rows,
}: FacultyFormGroupProps) {
  return (
    <Form.Group controlId={attribute}>
      <Form.Label>
        {label}
        {required && <span className="text-danger"> *</span>}
      </Form.Label>
      <Form.Control
        placeholder={placeholder}
        onChange={onChange}
        type={type || "text"}
        defaultValue={defaultValue}
        as={as}
        required={required}
        rows={rows}
      />
    </Form.Group>
  );
}
